import { Combobox, Icon } from "@simplybusiness/mobius";
import { useSnowplowContext } from "@simplybusiness/services";
import { useRef, useMemo, useCallback } from "react";
import { useQuestionnaire } from "../../../../../../../features/questionnaire";
import type { QuestionComponentProps } from "../../types";
import { search } from "@simplybusiness/icons";
import { getStateCode } from "../../../../../../../utils/getStateCode";

type StateOption = {
  value: string;
  label: string;
};

export const USStateQuestion = ({
  questionFieldProps,
  options = [],
}: QuestionComponentProps) => {
  const {
    value,
    onChange,
    onBlur,
    questionLabelProps,
    helpInfo,
    ...otherProps
  } = questionFieldProps;

  const { snowplow } = useSnowplowContext();
  const state = useQuestionnaire();

  const stateOptions = options as StateOption[];

  const hasFiredUsStateClicked = useRef(false);
  const handleUsStateFocus = () => {
    if (!hasFiredUsStateClicked.current) {
      hasFiredUsStateClicked.current = true;
      snowplow?.trigger("usStateClicked");
    }
  };

  // Memoize computed values to avoid recalculating on every render
  const { labelToValueMap, valueToLabelMap, optionsWithCodes } = useMemo(() => {
    // Create mapping from label to value
    const labelToValue = stateOptions.reduce(
      (acc, option) => {
        acc[option.label] = option.value;
        return acc;
      },
      {} as Record<string, string>,
    );

    // Create mapping from value to label for reverse lookup
    const valueToLabel = stateOptions.reduce(
      (acc, option) => {
        acc[option.value] = option.label;
        return acc;
      },
      {} as Record<string, string>,
    );

    const withCodes = stateOptions.map(option => ({
      ...option,
      code: getStateCode(option.label).toLowerCase(),
      lowerLabel: option.label.toLowerCase(),
    }));

    return {
      labelToValueMap: labelToValue,
      valueToLabelMap: valueToLabel,
      optionsWithCodes: withCodes,
    };
  }, [stateOptions]);

  const filterStateOptions = useCallback(
    (inputValue: string): string[] => {
      const lowerInput = inputValue.toLowerCase();

      const codeMatches: string[] = [];
      const startsWithMatches: string[] = [];
      const includesMatches: string[] = [];

      optionsWithCodes.forEach(({ label, code, lowerLabel }) => {
        if (code && code.includes(lowerInput)) {
          codeMatches.push(label);
        } else if (lowerLabel.startsWith(lowerInput)) {
          startsWithMatches.push(label);
        } else if (lowerLabel.includes(lowerInput)) {
          includesMatches.push(label);
        }
      });

      return [...codeMatches, ...startsWithMatches, ...includesMatches];
    },
    [optionsWithCodes],
  );

  const handleSelected = (selectedLabel: string) => {
    const mappedValue = labelToValueMap[selectedLabel] || selectedLabel;

    if (mappedValue) {
      snowplow?.trigger("stateQuestionAnswered", {
        answer: mappedValue,
        selectedCoverages: state.config?.selectedCoverages,
        selectedType: "coverage_selection",
      });
    }

    onChange?.({
      target: {
        name: questionFieldProps.name,
        value: mappedValue,
      },
    });
  };

  // Convert the incoming value to its corresponding label for display
  const displayValue = value ? valueToLabelMap[value as string] || value : "";

  return (
    <Combobox
      {...otherProps}
      icon={<Icon icon={search} />}
      value={displayValue as string}
      asyncOptions={filterStateOptions}
      delay={0}
      minSearchLength={0}
      onSelected={handleSelected}
      onFocus={handleUsStateFocus}
    />
  );
};
